import {
  Ban,
  CalendarCheck,
  CalendarX,
  Download,
  FilePen,
  FileUp,
  Lock,
  PlayCircle,
  PlusCircle,
  Shuffle,
  type LucideIcon,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { ErroApi } from '@/nucleo/auth/cliente-api';
import { formatarDataHora } from '@/nucleo/formato/datas';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';
import { CardSkeleton } from '@/ui/base/table-skeleton';
import { ConfirmDialog } from '@/ui/ConfirmDialog';
import { DetailHeader } from '@/ui/DetailHeader';
import { ErrorState } from '@/ui/ErrorState';
import { HistoryTimeline, type HistoryTimelineEntry } from '@/ui/HistoryTimeline';

import { CancelamentoDoCiclo, EnvioDoEdital } from './AcoesDoCiclo';
import { AvisoSementeDoSorteio } from './AvisoSementeDoSorteio';
import {
  type CicloDetalhe,
  type EventoDoCiclo,
  MODALIDADES,
  ROTULO_DA_ACAO,
  ROTULO_DA_MODALIDADE,
  ROTULO_DO_CAMPO,
  situacaoDoCiclo,
} from './contrato';
import {
  baixarEdital,
  lerComoBase64,
  mensagemDoErro,
  useAbrirCiclo,
  useAlterarCiclo,
  useCancelarCiclo,
  useDetalheDoCiclo,
  useEnviarEdital,
  useListaDeCiclos,
} from './dados';
import { FormularioDoCiclo } from './FormularioDoCiclo';

export const CAMINHO_DA_TELA = '/admin/parametros/ciclos-inscricao';

const ICONE_DA_ACAO: Record<string, { icone: LucideIcon; tom: string }> = {
  CRIAR: { icone: PlusCircle, tom: 'bg-muted text-muted-foreground' },
  ALTERAR: { icone: FilePen, tom: 'bg-primary/10 text-primary' },
  ENVIAR_EDITAL: { icone: FileUp, tom: 'bg-primary/10 text-primary' },
  ABRIR: { icone: PlayCircle, tom: 'bg-success/10 text-success' },
  ENCERRAR: { icone: Lock, tom: 'bg-muted text-muted-foreground' },
  SORTEAR: { icone: Shuffle, tom: 'bg-primary/10 text-primary' },
  CANCELAR: { icone: Ban, tom: 'bg-destructive/10 text-destructive' },
};

function paraLinhaDoTempo(e: EventoDoCiclo): HistoryTimelineEntry {
  const visual = ICONE_DA_ACAO[e.acao] ?? ICONE_DA_ACAO.ALTERAR!;
  const campos = e.alteracoes.map((c) => ROTULO_DO_CAMPO[c] ?? c).join(', ');
  return {
    id: e.id,
    icon: visual.icone,
    tone: visual.tom,
    titulo: ROTULO_DA_ACAO[e.acao] ?? 'Registro na trilha',
    etiqueta: e.origem === 'SISTEMA' ? 'Automático' : undefined,
    quando: formatarDataHora(e.criadoEm),
    por: e.origem === 'SISTEMA' ? 'Sistema' : (e.autor ?? null),
    descricao:
      [campos ? `Alterou: ${campos}.` : null, e.motivo ? `Motivo: ${e.motivo}` : null]
        .filter(Boolean)
        .join(' ') || null,
  };
}

function Dado({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0 space-y-0.5">
      <dt className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {rotulo}
      </dt>
      <dd className="wrap-break-word text-sm">{children}</dd>
    </div>
  );
}

/** O que ainda falta para abrir — mostrado em vez de um botão que seria recusado. */
function pendenciasParaAbrir(d: CicloDetalhe): string[] {
  const p: string[] = [];
  if (!d.edital) p.push('enviar o arquivo do edital');
  if (d.ciclo.combinacoes === 0) p.push('escolher DREs e modalidades e informar as vagas');
  if (!d.ciclo.fimEm || new Date(d.ciclo.fimEm).getTime() <= Date.now())
    p.push('ajustar o fim do período para uma data futura');
  return p;
}

export function DetalheDoCiclo({ id }: { id: string }) {
  const consulta = useDetalheDoCiclo(id);
  const lista = useListaDeCiclos(1);

  const alterar = useAlterarCiclo(id);
  const edital = useEnviarEdital(id);
  const abrir = useAbrirCiclo(id);
  const cancelar = useCancelarCiclo(id);

  const [modal, setModal] = useState<'alterar' | 'edital' | 'cancelar' | null>(null);

  function fechar() {
    setModal(null);
    alterar.reset();
    edital.reset();
    cancelar.reset();
  }

  const cabecalhoDeVolta = (
    <DetailHeader backTo={CAMINHO_DA_TELA} backLabel="Ciclos" title="Ciclo de inscrição" />
  );

  if (consulta.isLoading) {
    return (
      <div className="space-y-6">
        {cabecalhoDeVolta}
        <div role="status" aria-live="polite" className="space-y-4">
          <CardSkeleton />
          <CardSkeleton />
          <p className="sr-only">Carregando o ciclo.</p>
        </div>
      </div>
    );
  }

  if (consulta.isError || !consulta.data) {
    const naoExiste = consulta.error instanceof ErroApi && consulta.error.situacao === 404;
    return (
      <div className="space-y-6">
        {cabecalhoDeVolta}
        {naoExiste ? (
          <ErrorState
            variant="resource-not-found"
            title="Ciclo não encontrado"
            description="Volte à lista e escolha outro ciclo."
            primaryAction={{ label: 'Voltar aos ciclos', to: CAMINHO_DA_TELA }}
          />
        ) : (
          <ErrorState
            variant="load-failed"
            description="Não foi possível carregar o ciclo agora. Tente de novo em instantes."
            primaryAction={{ label: 'Tentar de novo', onClick: () => void consulta.refetch() }}
          />
        )}
      </div>
    );
  }

  const d = consulta.data;
  const c = d.ciclo;
  const situacao = situacaoDoCiclo(c);
  const editavel = c.status === 'RASCUNHO' || c.status === 'ABERTO';
  const pendencias = c.status === 'RASCUNHO' ? pendenciasParaAbrir(d) : [];
  const podeCancelar =
    c.status === 'RASCUNHO' || (c.status === 'ABERTO' && c.totalInscricoes === 0);
  const modalidadesDaGrade = MODALIDADES.filter((m) => d.vagas.some((v) => v.modalidade === m));
  const dresDaGrade = [...new Map(d.vagas.map((v) => [v.dreId, v.dreNome])).entries()];

  const acoes = editavel ? (
    <>
      <Button variant="outline" onClick={() => setModal('alterar')} disabled={!lista.data}>
        <FilePen className="mr-2 h-4 w-4" aria-hidden />
        Alterar
      </Button>
      <Button variant="outline" onClick={() => setModal('edital')}>
        <FileUp className="mr-2 h-4 w-4" aria-hidden />
        {d.edital ? 'Trocar edital' : 'Enviar edital'}
      </Button>
      {podeCancelar && (
        <Button variant="outline" onClick={() => setModal('cancelar')}>
          <CalendarX className="mr-2 h-4 w-4" aria-hidden />
          Cancelar ciclo
        </Button>
      )}
      {c.status === 'RASCUNHO' && pendencias.length === 0 && (
        <ConfirmDialog
          trigger={
            <Button disabled={abrir.isPending}>
              <CalendarCheck className="mr-2 h-4 w-4" aria-hidden />
              Abrir ciclo
            </Button>
          }
          title="Abrir o ciclo de inscrição?"
          description={`O período fica público e as inscrições são aceitas de ${formatarDataHora(c.inicioEm)} a ${formatarDataHora(c.fimEm)}. Depois do encerramento, nada mais muda no ciclo.`}
          confirmLabel="Abrir ciclo"
          onConfirm={() =>
            abrir.mutate(
              { versao: c.versao },
              {
                onSuccess: () => toast.success('Ciclo aberto.'),
                onError: (erro) =>
                  toast.error('O ciclo não foi aberto', {
                    description: mensagemDoErro(erro, 'Tente de novo em instantes.'),
                  }),
              },
            )
          }
        />
      )}
    </>
  ) : null;

  return (
    <div className="space-y-6">
      <DetailHeader
        backTo={CAMINHO_DA_TELA}
        backLabel="Ciclos"
        title={c.nome}
        subtitle={`${formatarDataHora(c.inicioEm)} a ${formatarDataHora(c.fimEm)}`}
        status={{ variant: situacao.variante, label: situacao.rotulo }}
        actions={acoes}
      />

      <AvisoSementeDoSorteio />

      {pendencias.length > 0 && (
        <Card>
          <CardContent className="pt-6 text-sm">
            <p className="font-medium">Para abrir este ciclo, falta:</p>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              {pendencias.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {c.status === 'CANCELADO' && c.motivoCancelamento && (
        <Card>
          <CardContent className="pt-6 text-sm">
            <p className="font-medium">Cancelado em {formatarDataHora(c.canceladoEm)}</p>
            <p className="text-muted-foreground">{c.motivoCancelamento}</p>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Período e edital</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid gap-4 sm:grid-cols-2">
                <Dado rotulo="Início">{formatarDataHora(c.inicioEm)}</Dado>
                <Dado rotulo="Fim">{formatarDataHora(c.fimEm)}</Dado>
                <Dado rotulo="Versão do edital">{c.editalVersao ?? '—'}</Dado>
                <Dado rotulo="Arquivo do edital">
                  {d.edital ? (
                    <Button
                      variant="link"
                      className="h-auto p-0 text-link"
                      onClick={() =>
                        void baixarEdital(c.id, d.edital!.arquivoNome).catch(() =>
                          toast.error('Não foi possível baixar o edital', {
                            description: 'Tente de novo em instantes.',
                          }),
                        )
                      }
                    >
                      <Download className="mr-1 h-4 w-4" aria-hidden />
                      {d.edital.arquivoNome}
                    </Button>
                  ) : (
                    'Não enviado'
                  )}
                </Dado>
                <Dado rotulo="Teto de escolas por veículo">{c.maxEscolas ?? '—'}</Dado>
                <Dado rotulo="Prazo de resposta do pré-credenciado">
                  {c.prazoRespostaDias ? `${c.prazoRespostaDias} dias` : '—'}
                </Dado>
                <Dado rotulo="Inscrições recebidas">{c.totalInscricoes}</Dado>
                <Dado rotulo="Total de vagas">{c.totalVagas}</Dado>
                {c.descricao && (
                  <div className="sm:col-span-2">
                    <Dado rotulo="Descrição">{c.descricao}</Dado>
                  </div>
                )}
              </dl>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Vagas por DRE e modalidade</CardTitle>
            </CardHeader>
            <CardContent>
              {dresDaGrade.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Nenhuma DRE ou modalidade escolhida ainda.
                </p>
              ) : (
                <ul className="space-y-3">
                  {dresDaGrade.map(([dreId, dreNome]) => (
                    <li key={dreId} className="rounded-xl border border-border p-3">
                      <p className="mb-2 text-sm font-medium">{dreNome}</p>
                      <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                        {modalidadesDaGrade.map((m) => (
                          <Dado key={m} rotulo={ROTULO_DA_MODALIDADE[m]}>
                            {d.vagas.find((v) => v.dreId === dreId && v.modalidade === m)
                              ?.quantidade ?? '—'}
                          </Dado>
                        ))}
                      </dl>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>

        <Card className="h-fit">
          <CardHeader>
            <CardTitle>Trilha do ciclo</CardTitle>
          </CardHeader>
          <CardContent>
            <HistoryTimeline
              entradas={d.eventos.map(paraLinhaDoTempo)}
              vazioTitulo="Sem registros ainda"
              vazioDescricao="Cada ação sobre o ciclo aparece aqui, com autor e motivo."
            />
          </CardContent>
        </Card>
      </div>

      {modal === 'alterar' && lista.data && (
        <FormularioDoCiclo
          aberto
          aoFechar={fechar}
          dres={lista.data.dres}
          ciclo={d}
          enviando={alterar.isPending}
          erro={alterar.error ? mensagemDoErro(alterar.error, 'Não foi possível salvar.') : null}
          aoEnviar={(entrada, motivo) =>
            alterar.mutate(
              { ...entrada, versao: c.versao, motivo },
              {
                onSuccess: () => {
                  toast.success('Ciclo alterado.');
                  fechar();
                },
              },
            )
          }
        />
      )}

      {modal === 'edital' && (
        <EnvioDoEdital
          aberto
          aoFechar={fechar}
          exigeMotivo={c.status === 'ABERTO'}
          enviando={edital.isPending}
          erro={edital.error ? mensagemDoErro(edital.error, 'Não foi possível enviar.') : null}
          aoEnviar={(arquivo, motivo) =>
            void lerComoBase64(arquivo).then((conteudoBase64) =>
              edital.mutate(
                { versao: c.versao, arquivoNome: arquivo.name, conteudoBase64, motivo },
                {
                  onSuccess: () => {
                    toast.success('Edital enviado.');
                    fechar();
                  },
                },
              ),
            )
          }
        />
      )}

      {modal === 'cancelar' && (
        <CancelamentoDoCiclo
          aberto
          aoFechar={fechar}
          enviando={cancelar.isPending}
          erro={
            cancelar.error ? mensagemDoErro(cancelar.error, 'Não foi possível cancelar.') : null
          }
          aoConfirmar={(motivo) =>
            cancelar.mutate(
              { versao: c.versao, motivo },
              {
                onSuccess: () => {
                  toast.success('Ciclo cancelado.');
                  fechar();
                },
              },
            )
          }
        />
      )}
    </div>
  );
}
